export type BlockKind = "paragraph" | "heading" | "list" | "image" | "video" | "table" | "callout";

export interface ContentBlock {
  id: string;
  kind: BlockKind;
  text: string;
  items: string[];
  headers: string[];
  rows: string[][];
  alt: string;
  key: string;
  previewUrl: string;
}

const VIDEO_EXTENSION = /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i;

export const isVideoSource = (source: string) => VIDEO_EXTENSION.test(source);

const createId = () => crypto.randomUUID();

export const createBlock = (kind: BlockKind): ContentBlock => ({
  id: createId(),
  kind,
  text: "",
  items: kind === "list" ? [""] : [],
  headers: kind === "table" ? ["Coluna 1", "Coluna 2"] : [],
  rows: kind === "table" ? [["", ""]] : [],
  alt: "",
  key: "",
  previewUrl: "",
});

const escapeCell = (value: string) => value.replace(/\|/g, "/").replace(/\n/g, " ").trim();

const blockHasContent = (block: ContentBlock) => {
  if (block.kind === "list") return block.items.some((item) => item.trim());
  if (block.kind === "table") return block.rows.some((row) => row.some((cell) => cell.trim()));
  if (block.kind === "image" || block.kind === "video") return Boolean(block.key.trim());
  return Boolean(block.text.trim());
};

export const blocksToMarkdown = (blocks: ContentBlock[]) =>
  blocks
    .filter(blockHasContent)
    .map((block) => {
      if (block.kind === "heading") return `## ${block.text.trim()}`;
      if (block.kind === "list") return block.items.filter((item) => item.trim()).map((item) => `- ${item.trim()}`).join("\n");
      if (block.kind === "callout") return block.text.trim().split("\n").map((line) => `> ${line}`).join("\n");
      if (block.kind === "image" || block.kind === "video") return `![${block.alt.trim()}](${block.key.trim()})`;
      if (block.kind === "table") {
        const headers = block.headers.map(escapeCell);
        const divider = headers.map(() => "---");
        const rows = block.rows.map((row) => headers.map((_, index) => escapeCell(row[index] ?? "")));
        return [headers, divider, ...rows].map((cells) => `| ${cells.join(" | ")} |`).join("\n");
      }
      return block.text.trim();
    })
    .join("\n\n");

const destinations = (markdown: string) => [...markdown.matchAll(/\]\(<?([^)\s>]+)>?(?:\s+"[^"]*")?\)/g)].map((match) => match[1]);

export const KNOWLEDGE_BASE_MEDIA_PREFIX = "knowledge-base/";

export const withPreviewUrls = (markdown: string, previewByKey: Map<string, string>) =>
  markdown.replace(/\]\(<?([^)\s>]+)>?((?:\s+"[^"]*")?)\)/g, (match, key: string, title: string) => {
    const url = previewByKey.get(key);
    return url ? `](${url}${title})` : match;
  });

export const previewUrlsByKey = (raw: string, rendered: string, mediaPrefix = KNOWLEDGE_BASE_MEDIA_PREFIX) => {
  const stored = destinations(raw);
  const signed = destinations(rendered);
  const urls = new Map<string, string>();

  stored.forEach((key, index) => {
    const url = signed[index];
    if (key.startsWith(mediaPrefix) && url && url !== key) urls.set(key, url);
  });

  return urls;
};

const parseMedia = (alt: string, source: string, previewByKey: Map<string, string>, mediaPrefix: string): ContentBlock => {
  const isStoredKey = source.startsWith(mediaPrefix);
  const kind = isVideoSource(source) ? "video" : "image";

  return {
    ...createBlock(kind),
    alt,
    key: source,
    previewUrl: isStoredKey ? (previewByKey.get(source) ?? "") : source,
  };
};

const parseTable = (chunk: string): ContentBlock => {
  const lines = chunk.split("\n").map((line) => line.trim()).filter(Boolean);
  const cellsOf = (line: string) => line.replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
  const headers = cellsOf(lines[0] ?? "");
  const body = lines.slice(2).map(cellsOf);

  return {
    ...createBlock("table"),
    headers: headers.length ? headers : ["Coluna 1", "Coluna 2"],
    rows: body.length ? body.map((row) => headers.map((_, index) => row[index] ?? "")) : [headers.map(() => "")],
  };
};

const parseChunk = (chunk: string, previewByKey: Map<string, string>, mediaPrefix: string): ContentBlock => {
  const media = chunk.match(/^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)\s*$/);
  if (media) return parseMedia(media[1], media[2], previewByKey, mediaPrefix);

  const heading = chunk.match(/^##\s+([\s\S]+)$/);
  if (heading && !chunk.includes("\n")) return { ...createBlock("heading"), text: heading[1].trim() };

  if (chunk.startsWith("|")) return parseTable(chunk);

  const lines = chunk.split("\n").filter((line) => line.trim());
  if (lines.length && lines.every((line) => /^[-*]\s+/.test(line))) {
    return { ...createBlock("list"), items: lines.map((line) => line.replace(/^[-*]\s+/, "")) };
  }

  if (lines.length && lines.every((line) => line.startsWith(">"))) {
    return { ...createBlock("callout"), text: lines.map((line) => line.replace(/^>\s?/, "")).join("\n") };
  }

  return { ...createBlock("paragraph"), text: chunk };
};

export const markdownToBlocks = (markdown: string, previewByKey = new Map<string, string>(), mediaPrefix = KNOWLEDGE_BASE_MEDIA_PREFIX): ContentBlock[] => {
  const source = markdown.trim();
  if (!source) return [];

  return source.split(/\n{2,}/).map((chunk) => parseChunk(chunk.trim(), previewByKey, mediaPrefix)).filter((block) => block.text || block.items.length || block.key || block.rows.length);
};
